import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['oem', 'region', 'territory', 'states_covered', 'warranty_years', 'primary_name', 'primary_email', 'primary_phone', 'esc1_name', 'esc1_email', 'esc1_phone', 'final_name', 'final_email', 'final_phone'];

const EMPTY_FORM = {
  oem: '', region: '', territory: '', states_covered: [], warranty_years: 5,
  mode_of_replacement: '', contract_terms: '', issue_type: '',
  primary_name: '', primary_email: '', primary_phone: '',
  esc1_name: '', esc1_email: '', esc1_phone: '',
  final_name: '', final_email: '', final_phone: '',
  address_line1: '', address_line2: '', address_city: '', address_state: '', address_pincode: '',
  is_active: true,
};

const TABLE_COLS = [
  { key: 'oem', label: 'OEM' },
  { key: 'region', label: 'Region' },
  { key: 'territory', label: 'Territory' },
  { key: 'primary_name', label: 'Primary Contact' },
  { key: 'primary_phone', label: 'Phone' },
  { key: 'esc1_name', label: 'Escalation 1' },
  { key: 'final_name', label: 'Final Escalation' },
  { key: 'is_active', label: 'Active', render: v => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{v ? 'Yes' : 'No'}</span>
  )},
];

export default function VendorContacts() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await client.get('/admin/vendor-contacts/');
      setData(Array.isArray(res) ? res : res.results ?? []);
    } catch { setError('Failed to load vendor contacts.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => {
    setEditRow(row);
    const statesText = Array.isArray(row.states_covered) ? row.states_covered.join('\n') : (row.states_covered || '');
    setForm({ ...row, _states_text: statesText });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      const statesText = payload._states_text || '';
      payload.states_covered = statesText.split('\n').map(s => s.trim()).filter(Boolean);
      delete payload._states_text;
      if (editRow) {
        await client.put(`/admin/vendor-contacts/${editRow.id}/`, payload);
      } else {
        await client.post('/admin/vendor-contacts/', payload);
      }
      setShowForm(false);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || JSON.stringify(err?.response?.data) || 'Save failed.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await client.delete(`/admin/vendor-contacts/${deleteRow.id}/`);
      setDeleteRow(null);
      load();
    } catch { alert('Delete failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/vendor-contacts/', fd);
    load();
    return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  const SectionLabel = ({ children }) => (
    <div className="col-span-2 mt-2 mb-1">
      <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider border-b border-purple-100 pb-1">{children}</p>
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Vendor Contacts</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage OEM vendor escalation contacts</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Vendor Contact
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable columns={TABLE_COLS} data={data} loading={loading} onEdit={openEdit} onDelete={setDeleteRow} />
      </div>

      {showUpload && (
        <ExcelUploadModal title="Vendor Contacts" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="vendor-contacts" />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Vendor Contact' : 'Add Vendor Contact'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-2 gap-4">
              <SectionLabel>Basic Info</SectionLabel>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">OEM *</label><input className={inp} value={form.oem} onChange={e => set('oem', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Region</label><input className={inp} value={form.region} onChange={e => set('region', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Territory</label><input className={inp} value={form.territory} onChange={e => set('territory', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Warranty Years</label><input type="number" className={inp} value={form.warranty_years} onChange={e => set('warranty_years', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Mode of Replacement</label><input className={inp} value={form.mode_of_replacement} onChange={e => set('mode_of_replacement', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Contract Terms</label><input className={inp} value={form.contract_terms} onChange={e => set('contract_terms', e.target.value)} /></div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-600 mb-1 block">States Covered <span className="text-gray-400 font-normal">(one state per line)</span></label>
                <textarea className={inp} rows={3} value={form._states_text || (Array.isArray(form.states_covered) ? form.states_covered.join('\n') : '')} onChange={e => set('_states_text', e.target.value)} placeholder="Maharashtra&#10;Karnataka&#10;Telangana" />
              </div>

              <SectionLabel>Primary Contact</SectionLabel>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Name</label><input className={inp} value={form.primary_name} onChange={e => set('primary_name', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Email</label><input type="email" className={inp} value={form.primary_email} onChange={e => set('primary_email', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label><input className={inp} value={form.primary_phone} onChange={e => set('primary_phone', e.target.value)} /></div>

              <SectionLabel>Escalation 1</SectionLabel>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Name</label><input className={inp} value={form.esc1_name} onChange={e => set('esc1_name', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Email</label><input type="email" className={inp} value={form.esc1_email} onChange={e => set('esc1_email', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label><input className={inp} value={form.esc1_phone} onChange={e => set('esc1_phone', e.target.value)} /></div>

              <SectionLabel>Final Escalation</SectionLabel>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Name</label><input className={inp} value={form.final_name} onChange={e => set('final_name', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Email</label><input type="email" className={inp} value={form.final_email} onChange={e => set('final_email', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label><input className={inp} value={form.final_phone} onChange={e => set('final_phone', e.target.value)} /></div>

              <div className="col-span-2 flex items-center gap-2 mt-2">
                <input type="checkbox" id="vc_active" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                <label htmlFor="vc_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Vendor Contact</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.oem} â€“ {deleteRow.region}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

